import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import { FolderOpen, FileEdit } from 'lucide-react';
import { BlogTypesSection } from './components/BlogTypesSection';
import { BlogPostsQueue } from './components/BlogPostsQueue';

type BlogTab = 'posts' | 'types';

const STORAGE_KEY = 'admin.blog.activeTab';
const VALID_TABS: BlogTab[] = ['posts', 'types'];

function isBlogTab(v: string | null | undefined): v is BlogTab {
    return Boolean(v && VALID_TABS.includes(v as BlogTab));
}

function readStoredTab(): BlogTab {
    try {
        const v = sessionStorage.getItem(STORAGE_KEY);
        if (isBlogTab(v)) return v;
    } catch { /* ignore */ }
    return 'posts';
}

const TABS: { value: BlogTab; label: string; description: string; icon: React.ReactNode }[] = [
    { value: 'posts', label: 'Entradas', description: 'Cola editorial del blog: estado, vista previa e import masivo', icon: <FileEdit size={18} /> },
    { value: 'types', label: 'Tipologías', description: 'Categorías del blog (consejos, legal, review…)', icon: <FolderOpen size={18} /> },
];

export function BlogPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabFromUrl = searchParams.get('tab');
    const tab: BlogTab = isBlogTab(tabFromUrl) ? tabFromUrl : readStoredTab();

    useEffect(() => {
        if (!isBlogTab(tabFromUrl)) {
            setSearchParams({ tab }, { replace: true });
        }
        try { sessionStorage.setItem(STORAGE_KEY, tab); } catch { /* ignore */ }
    }, [tab, tabFromUrl, setSearchParams]);

    const setTabPersist = (next: BlogTab) => {
        setSearchParams({ tab: next }, { replace: true });
        try { sessionStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    };

    const activeTab = TABS.find((t) => t.value === tab);

    return (
        <div className="space-y-6">
            <PageHeader
                title="Blog"
                description={activeTab?.description}
            />

            <div className="flex gap-1 p-1 bg-muted/50 rounded-lg w-fit">
                {TABS.map((t) => (
                    <button
                        key={t.value}
                        type="button"
                        onClick={() => setTabPersist(t.value)}
                        className={cn(
                            'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all',
                            tab === t.value
                                ? 'bg-primary text-primary-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        {t.icon}
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === 'posts' && <BlogPostsQueue />}
            {tab === 'types' && <BlogTypesSection />}
        </div>
    );
}
